import { Button, Flex, Grid } from '@chatterbug/aaron'
import React, { useEffect, useState } from 'react'
import { t } from 'src/lib/i18n-react'
import GoalPredictionSideNote, {
  Props as GoalPredictionSideNoteProps,
} from 'src/pages/GoalSetting/subcomponents/GoalPredictionSideNote/GoalPredictionSideNote'
import GoalSlider, {
  GoalSettingType,
} from 'src/pages/GoalSetting/subcomponents/GoalSlider/GoalSlider'
import { CefrLevelCode, Plan } from 'src/types'
import { getLocale } from 'src/config/globals'
import DateUtil from 'src/lib/date-util'

const LOCALE = getLocale()

export type Props = {
  liveLessonGoal: number
  selfStudyGoal: number
  sideNoteData: SideNote[]
  onSaveGoals: (liveLessonsCount?: number, selfStudyMinutes?: number) => void
  goalCefrLevel: CefrLevelCode
}

type SideNote = {
  liveLessonsPerWeek: number
  recommendedPlan: Plan
  predictedGoalCompletionDate: string
}

const SUFFIXES = {
  beginning: t('beginning of'),
  middle: t('middle'),
  end: t('end of'),
}

const timeSuffix = (date: number) => {
  if (date < 11) {
    return SUFFIXES['beginning']
  } else if (date > 19) {
    return SUFFIXES['end']
  } else {
    return SUFFIXES['middle']
  }
}

const formatDate = (date: string) => {
  const momentDate = DateUtil.dateFromISOString(date, LOCALE)
  const suffix = timeSuffix(momentDate.date())
  return momentDate.format(`[${suffix}] MMMM YYYY`)
}

const GoalForm: React.FC<Props> = ({
  liveLessonGoal,
  selfStudyGoal,
  onSaveGoals,
  sideNoteData,
  goalCefrLevel,
}) => {
  const [sideNote, setSideNote] = useState<GoalPredictionSideNoteProps>(null)
  const [liveLessonsCount, setLiveLessonCount] = useState(liveLessonGoal)
  const [selfStudyMinutes, setSelfStudyMinutes] = useState(selfStudyGoal)
  const monthlyLiveLessons = liveLessonsCount * 4

  useEffect(() => {
    const { predictedGoalCompletionDate, recommendedPlan } = sideNoteData.find(
      (r: SideNote) => r.liveLessonsPerWeek === liveLessonsCount
    )

    if (predictedGoalCompletionDate) {
      setSideNote({
        goalCefrLevel: goalCefrLevel,
        predictedNumberOfLessonsPerMonth: monthlyLiveLessons,
        predictedDateOfCompletion: formatDate(predictedGoalCompletionDate),
        recommendedPlan: recommendedPlan,
      })
    } else {
      setSideNote(null)
    }
  }, [liveLessonsCount])

  const handleLiveLessonChange = (value: number) => {
    if (value == liveLessonsCount) {
      return
    }
    setLiveLessonCount(value)
  }

  const handleSaveGoals = (event: any) => {
    event.preventDefault()
    onSaveGoals(liveLessonsCount, selfStudyMinutes)
  }

  return (
    <Grid gap="3x" as="form">
      <Grid
        columns={{ _: '1fr', tablet: '1fr 1fr' }}
        gap="3x"
        mt={{ _: '4x', tablet: '5x' }}
      >
        <GoalSlider
          type={GoalSettingType.liveLesson}
          onChange={handleLiveLessonChange}
          goalValue={liveLessonsCount}
        />
        <GoalSlider
          type={GoalSettingType.selfStudy}
          onChange={setSelfStudyMinutes}
          goalValue={selfStudyMinutes}
        />
      </Grid>
      {sideNote && <GoalPredictionSideNote {...sideNote} />}
      <Flex flexDirection={{ _: 'column', tablet: 'row' }}>
        <Button
          onClick={handleSaveGoals}
          type="primaryAdvanced"
          //TODO add disabled to here when available in Aaron buttons
        >
          {t('Update Goals')}
        </Button>
      </Flex>
    </Grid>
  )
}

export default GoalForm
